// src/pages/TransferPage.js
import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { getItemById, getWorkers, getLocations, initiatePendingTransfer, createTransaction } from '../services/api';
import JobPicker from '../components/JobPicker';

// Roles that can transfer to a truck/location in addition to a person
const LOCATION_TRANSFER_ROLES = ['foreman', 'site_supervisor', 'general_superintendent', 'project_manager', 'project_engineer', 'project_administrator', 'power_user', 'driver'];

export default function TransferPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { worker } = useContext(AuthContext);
  const role = worker?.role || 'worker';

  const canTransferToLocation = LOCATION_TRANSFER_ROLES.includes(role);

  const [mode, setMode] = useState('person'); // 'person' | 'location'
  const [item, setItem] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [jobsites, setJobsites] = useState([]);
  const [places, setPlaces] = useState([]);   // where a person's tool can go: jobsites, warehouse, other
  const [jobSource, setJobSource] = useState(null);
  const [search, setSearch] = useState('');
  const [filtered, setFiltered] = useState([]);

  // Two-step truck flow: selectedTruck → selectedJobsite
  const [selectedTruck, setSelectedTruck] = useState(null);
  const [selectedJobsite, setSelectedJobsite] = useState(null);
  // Person mode. personJob: undefined = not asked yet, null = skipped,
  // { loc, source } = the job the tool goes to (10/29).
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [personJob, setPersonJob] = useState(undefined);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  // Which step are we on in location mode?
  // 'truck' → picking truck, 'jobsite' → picking jobsite, 'confirm' → ready to submit
  const locationStep = !selectedTruck ? 'truck'
    : selectedTruck.location_type === 'truck' && !selectedJobsite ? 'jobsite'
    : 'confirm';

  useEffect(() => { loadData(); }, [id]);

  // Filter workers
  useEffect(() => {
    if (mode !== 'person') return;
    const q = search.toLowerCase();
    setFiltered(
      workers.filter(w =>
        w.worker_id !== worker?.worker_id &&
        (search.trim().length < 1 ||
          w.display_name?.toLowerCase().includes(q) ||
          w.first_name?.toLowerCase().includes(q) ||
          w.last_name?.toLowerCase().includes(q))
      )
    );
  }, [search, workers, mode]);

  // Filter locations (trucks)
  useEffect(() => {
    if (mode !== 'location' || locationStep !== 'truck') return;
    const q = search.toLowerCase();
    setFiltered(
      locations.filter(l =>
        search.trim().length < 1 ||
        l.name?.toLowerCase().includes(q) ||
        l.location_type?.toLowerCase().includes(q)
      )
    );
  }, [search, locations, mode, locationStep]);

  const loadData = async () => {
    try {
      const calls = [getItemById(id), getWorkers(), getLocations()];
      if (canTransferToLocation) calls.push(getLocations({ type: 'truck' }));
      const [itemRes, workersRes, placesRes, trucksRes] = await Promise.all(calls);
      const all = placesRes.data.locations || [];
      setPlaces(all.filter(l => ['jobsite', 'warehouse', 'external'].includes(l.location_type)));
      setJobsites(all.filter(l => l.location_type === 'jobsite'));
      setItem(itemRes.data.item);
      const allWorkers = workersRes.data.workers || [];
      setWorkers(allWorkers);
      setFiltered(allWorkers.filter(w => w.worker_id !== worker?.worker_id));
      if (trucksRes)   setLocations(trucksRes.data.locations || []);
    } catch {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setSelectedTruck(null);
    setSelectedJobsite(null);
    setSelectedPerson(null);
    setPersonJob(undefined);
    setSearch('');
    setError('');
    if (newMode === 'person') {
      setFiltered(workers.filter(w => w.worker_id !== worker?.worker_id));
    } else {
      setFiltered(locations);
    }
  };

  const handleSelectTruck = (loc) => {
    setSelectedTruck(loc);
    setSearch('');
    if (loc.location_type !== 'truck') {
      // Non-truck locations (warehouse, external) skip jobsite step
      setSelectedJobsite(null);
    }
  };

  const handlePersonTransfer = async () => {
    if (!selectedPerson) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await initiatePendingTransfer({
        item_id: id, to_worker_id: selectedPerson.worker_id, to_location_id: personJob?.loc.location_id || null,
      });
      setResult({ type: 'person', recipientName: res.data.recipient_name, sms_sent: res.data.sms_sent });
    } catch (err) {
      setError(err.response?.data?.error || 'Transfer failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLocationTransfer = async () => {
    if (!selectedTruck) return;
    setSubmitting(true);
    setError('');
    try {
      await createTransaction({
        item_id:              id,
        transaction_type:     'load_truck',
        performed_by:         worker.worker_id,
        to_location_id:       selectedTruck.location_id,
        quantity:             1,
        procore_project_id:   selectedJobsite?.procore_project_id   || null,
        procore_project_name: selectedJobsite?.procore_project_name || selectedJobsite?.name || null,
        job_pick_source:      jobSource,
        notes: `Loaded onto ${selectedTruck.name}${selectedJobsite ? ` → ${selectedJobsite.name}` : ''} by ${worker.display_name}`,
      });
      setResult({
        type:         'location',
        truckName:    selectedTruck.name,
        jobsiteName:  selectedJobsite?.name || null,
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Transfer failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Loading ──────────────────────────────────────────────
  if (loading) return (
    <div className="screen">
      <div className="loading"><div className="spinner" /><span>Loading...</span></div>
    </div>
  );

  // ── Success ──────────────────────────────────────────────
  if (result) {
    const isPending = result.type === 'person';
    return (
      <div className="screen">
        <div className="screen-content" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <div style={{ textAlign: 'center', padding: 40 }}>
            <div style={{ fontSize: '4rem', marginBottom: 16 }}>{isPending ? '⏳' : '✅'}</div>
            <h2 style={{ marginBottom: 8 }}>{isPending ? 'Transfer Pending' : 'Loaded!'}</h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: 8 }}><strong>{item?.name}</strong></p>
            {isPending ? (
              <>
                <p style={{ color: 'var(--text-muted)', marginBottom: 8 }}>
                  Waiting for <strong style={{ color: 'var(--text-primary)' }}>{result.recipientName}</strong> to accept.
                </p>
                {result.sms_sent
                  ? <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: 32 }}>SMS sent with acceptance link.</p>
                  : <p style={{ color: 'var(--status-in-repair)', fontSize: '0.85rem', marginBottom: 32 }}>SMS could not be sent — they'll need to accept via the app.</p>
                }
              </>
            ) : (
              <p style={{ color: 'var(--text-muted)', marginBottom: 32 }}>
                On <strong style={{ color: 'var(--text-primary)' }}>{result.truckName}</strong>
                {result.jobsiteName && <> → <strong style={{ color: 'var(--text-primary)' }}>{result.jobsiteName}</strong></>}
              </p>
            )}
            <button className="btn btn-primary" onClick={() => navigate('/')}>Back to Home</button>
            <button className="btn btn-secondary" style={{ marginTop: 10 }} onClick={() => navigate('/scan')}>Scan Another</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Main UI ──────────────────────────────────────────────
  const showingJobsitePicker = mode === 'location' && locationStep === 'jobsite';
  const showingConfirm       = mode === 'location' && locationStep === 'confirm';
  const showingPersonJob     = mode === 'person' && selectedPerson && personJob === undefined;
  const showingPersonConfirm = mode === 'person' && selectedPerson && personJob !== undefined;

  return (
    <div className="screen">
      <div className="screen-header">
        <button onClick={() => {
          if (showingJobsitePicker) { setSelectedTruck(null); setSearch(''); }
          else if (showingConfirm) { setSelectedJobsite(null); setSearch(''); }
          else if (showingPersonConfirm) { setPersonJob(undefined); }
          else if (showingPersonJob) { setSelectedPerson(null); setSearch(''); }
          else navigate(-1);
        }} style={{ background: 'none', border: 'none', color: 'var(--text-primary)', cursor: 'pointer' }}>
          <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div>
          <h2>{showingJobsitePicker ? 'Destination Jobsite' : showingPersonJob ? 'Which Job?' : showingConfirm || showingPersonConfirm ? 'Confirm Transfer' : 'Transfer Item'}</h2>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{item?.name}</p>
        </div>
      </div>

      <div className="screen-content">
        {error && <div className="alert alert-error" style={{ marginBottom: 12 }}>{error}</div>}

        {/* Item summary — only on first step */}
        {!showingJobsitePicker && !showingConfirm && !showingPersonConfirm && !showingPersonJob && (
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3>{item?.name}</h3>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                  {item?.category_name}{item?.battery_platform ? ` · ${item.battery_platform}` : ''}
                </p>
              </div>
              <span className="asset-tag">{item?.asset_tag}</span>
            </div>
            <div className="divider" />
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
              Currently held by: <strong>{item?.assigned_to_name || 'Warehouse'}</strong>
            </p>
          </div>
        )}

        {/* Mode toggle */}
        {canTransferToLocation && !showingJobsitePicker && !showingConfirm && !showingPersonConfirm && !showingPersonJob && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <button className={mode === 'person' ? 'btn btn-primary' : 'btn btn-secondary'} style={{ flex: 1 }} onClick={() => switchMode('person')}>
              👤 Person
            </button>
            <button className={mode === 'location' ? 'btn btn-primary' : 'btn btn-secondary'} style={{ flex: 1 }} onClick={() => switchMode('location')}>
              🚛 Truck
            </button>
          </div>
        )}

        {/* ── Person picker ── */}
        {mode === 'person' && !selectedPerson && (
          <>
            <input className="input" placeholder="Search by name..." value={search} onChange={e => setSearch(e.target.value)} style={{ marginBottom: 8 }} autoFocus />
            {filtered.length === 0 ? (
              <div className="empty-state"><p>No workers found{search ? ` for "${search}"` : ''}</p></div>
            ) : (
              <div className="card">
                {filtered.map(w => (
                  <div key={w.worker_id} className="item-row" onClick={() => { setSelectedPerson(w); setPersonJob(undefined); }} style={{ cursor: 'pointer' }}>
                    <div className="item-row-icon">👤</div>
                    <div className="item-row-info">
                      <h4>{w.display_name}</h4>
                      <p>{w.role}{w.division_name ? ` · ${w.division_name}` : ''}</p>
                    </div>
                    <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ── Truck picker ── */}
        {mode === 'location' && locationStep === 'truck' && (
          <>
            <input className="input" placeholder="Search trucks..." value={search} onChange={e => setSearch(e.target.value)} style={{ marginBottom: 8 }} autoFocus />
            {filtered.length === 0 ? (
              <div className="empty-state"><p>No trucks found{search ? ` for "${search}"` : ''}</p></div>
            ) : (
              <div className="card">
                {filtered.map(l => (
                  <div key={l.location_id} className="item-row" onClick={() => handleSelectTruck(l)} style={{ cursor: 'pointer' }}>
                    <div className="item-row-icon">🚛</div>
                    <div className="item-row-info">
                      <h4>{l.name}</h4>
                      <p style={{ textTransform: 'capitalize' }}>{l.location_type?.replace('_', ' ')}</p>
                    </div>
                    <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ── Jobsite picker (truck flow step 2) ── */}
        {showingJobsitePicker && (
          <>
            <div className="card" style={{ marginBottom: 12, background: 'var(--bg-secondary)' }}>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Truck</p>
              <p style={{ fontWeight: 600 }}>🚛 {selectedTruck.name}</p>
            </div>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: 8 }}>Where is this truck delivering?</p>
            <JobPicker locations={jobsites} forWorker={worker} allTitle="All jobs"
              onPick={(j, source) => { setSelectedJobsite(j); setJobSource(source); setSearch(''); }} />
          </>
        )}

        {/* ── Job for a person transfer (suggested from where you are + their Labor Board jobs) ── */}
        {showingPersonJob && (
          <>
            <div className="card" style={{ marginBottom: 12, background: 'var(--bg-secondary)' }}>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>To</p>
              <p style={{ fontWeight: 600 }}>👤 {selectedPerson.display_name}</p>
            </div>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: 8 }}>Which job is the tool going to?</p>
            <button className="btn btn-ghost" style={{ marginBottom: 12 }} onClick={() => setPersonJob(null)}>
              Skip: keep its current location
            </button>
            <JobPicker locations={places} forWorker={selectedPerson}
              onPick={(l, source) => setPersonJob({ loc: l, source })} />
          </>
        )}

        {/* ── Person confirmation ── */}
        {showingPersonConfirm && (
          <ConfirmCard
            item={item}
            from={worker?.display_name}
            toIcon="👤"
            toName={selectedPerson.display_name}
            toSub={personJob ? `@ ${personJob.loc.name}` : selectedPerson.role}
            note={`${selectedPerson.display_name} will be notified and must accept within 24 hours${personJob ? '. The tool moves to that job when accepted.' : ''}`}
            submitting={submitting}
            onConfirm={handlePersonTransfer}
            onBack={() => setPersonJob(undefined)}
            confirmLabel="📱 Send Transfer Request"
            backLabel="← Choose a Different Job"
          />
        )}

        {/* ── Location confirmation ── */}
        {showingConfirm && (
          <ConfirmCard
            item={item}
            from={worker?.display_name}
            toIcon="🚛"
            toName={selectedTruck.name}
            toSub={selectedJobsite ? `→ ${selectedJobsite.name}` : ''}
            note={selectedJobsite
              ? `Tool will be in transit to ${selectedJobsite.name} until delivered`
              : 'Tool will be marked in transit on this truck'}
            submitting={submitting}
            onConfirm={handleLocationTransfer}
            onBack={() => { setSelectedJobsite(null); setSelectedTruck(null); }}
            confirmLabel="🚛 Confirm Load"
            backLabel="← Choose a Different Truck"
          />
        )}
      </div>
    </div>
  );
}

function ConfirmCard({ item, from, toIcon, toName, toSub, note, submitting, onConfirm, onBack, confirmLabel, backLabel }) {
  return (
    <>
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '8px 0' }}>
          <div style={{ textAlign: 'center', flex: 1 }}>
            <div style={{ fontSize: '1.5rem', marginBottom: 4 }}>👤</div>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>From</p>
            <p style={{ fontWeight: 600 }}>{from}</p>
          </div>
          <div style={{ fontSize: '1.5rem', color: 'var(--ngc-orange)' }}>→</div>
          <div style={{ textAlign: 'center', flex: 1 }}>
            <div style={{ fontSize: '1.5rem', marginBottom: 4 }}>{toIcon}</div>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>To</p>
            <p style={{ fontWeight: 600 }}>{toName}</p>
            {toSub && <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{toSub}</p>}
          </div>
        </div>
        <div className="divider" />
        <p style={{ textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>{item?.name}</p>
        <p style={{ textAlign: 'center', fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 4 }}>{note}</p>
      </div>
      <button className="btn btn-primary" onClick={onConfirm} disabled={submitting}>
        {submitting ? 'Processing...' : confirmLabel}
      </button>
      <button className="btn btn-secondary" style={{ marginTop: 10 }} onClick={onBack} disabled={submitting}>
        {backLabel}
      </button>
    </>
  );
}
